import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDashboard } from '../contexts/DashboardContext';
import { apiFetch } from '../utils/api';
import { teamAcronym } from '../utils/teamAcronyms';
import Matrix3D from './Matrix3D';

function computeWindow(center, total, windowSize) {
  let start = Math.round(center - windowSize / 2);
  start = Math.max(0, Math.min(start, total - windowSize));
  return start;
}

function makePosColorFn(selectedRowIdx) {
  return (val, maxVal, rowIdx) => {
    const intensity = Math.min(1, val / maxVal);
    if (rowIdx === selectedRowIdx) return { color: '#dc3545', opacity: 0.5 + intensity * 0.5 };
    return { color: '#0d6efd', opacity: 0.4 + intensity * 0.5 };
  };
}

export default function PositionProbabilitiesCard() {
  const { token } = useAuth();
  const { selectedTeam, selectedDate } = useDashboard();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token || !selectedTeam || !selectedDate) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    apiFetch(`/position-probabilities?team=${encodeURIComponent(selectedTeam)}&date=${selectedDate}`, token)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [token, selectedTeam, selectedDate]);

  const { matrix, rowLabels, colLabels, selectedRowIdx } = useMemo(() => {
    if (!data || data.rows.length === 0) return { matrix: null, rowLabels: [], colLabels: [], selectedRowIdx: -1 };

    const selectedRow = data.rows.find((r) => r.selected);
    if (!selectedRow) {
      const rows = data.rows.slice(0, 10);
      return {
        matrix: rows.map((r) => r.probabilities.slice(0, 10)),
        rowLabels: rows.map((r) => teamAcronym(r.team)),
        colLabels: data.positions.slice(0, 10),
        selectedRowIdx: -1,
      };
    }

    let bestPosIdx = 0;
    let bestProb = -1;
    selectedRow.probabilities.forEach((p, i) => {
      if (p > bestProb) { bestProb = p; bestPosIdx = i; }
    });

    const teamIdx = data.rows.indexOf(selectedRow);
    const teamWindow = 10;
    const posWindow = 10;

    const teamStart = computeWindow(teamIdx, data.rows.length, Math.min(teamWindow, data.rows.length));
    const posStart = computeWindow(bestPosIdx, data.positions.length, Math.min(posWindow, data.positions.length));

    const slicedRows = data.rows.slice(teamStart, teamStart + Math.min(teamWindow, data.rows.length));
    const slicedPositions = data.positions.slice(posStart, posStart + Math.min(posWindow, data.positions.length));

    return {
      matrix: slicedRows.map((row) =>
        row.probabilities.slice(posStart, posStart + Math.min(posWindow, data.positions.length))
      ),
      rowLabels: slicedRows.map((r) => teamAcronym(r.team)),
      colLabels: slicedPositions,
      selectedRowIdx: teamIdx - teamStart,
    };
  }, [data]);

  const posColorFn = useMemo(() => makePosColorFn(selectedRowIdx), [selectedRowIdx]);

  return (
    <div className="card h-100">
      <div className="card-header d-flex align-items-center">
        <strong>Finishing Probabilities</strong>
        <span className="ms-auto text-muted small">Position distribution</span>
      </div>
      <div className="card-body p-2">
        {loading && (
          <div className="text-center py-4"><div className="spinner-border spinner-border-sm" /></div>
        )}
        {error && (
          <div className="text-center py-4 text-danger small">{error}</div>
        )}
        {!loading && !error && !matrix && (
          <div className="text-center py-4 text-muted small">No data available</div>
        )}
        {!loading && !error && matrix && (
          <>
            <div className="text-center text-muted" style={{ fontSize: '0.6em' }}>
              Teams (rows) v Positions (cols) &mdash; drag to rotate
            </div>
            <Matrix3D
              matrix={matrix}
              rowLabels={rowLabels}
              colLabels={colLabels}
              maxHeight={5}
              colorFn={posColorFn}
              height={280}
            />
          </>
        )}
      </div>
    </div>
  );
}
